import { useNow } from "@/components/ClockProvider";
import { Pill, type PillVariant } from "@/components/ui/Pill";
import { Tooltip } from "@/components/ui/Tooltip";
import { healthDescription, healthLabel } from "@/lib/strings";
import { absoluteTime, timeAgo } from "@/lib/utils";
import type { DeviceHealth } from "@/types/fleet";

const ONLINE_WINDOW_MS = 5 * 60_000;
const STALE_WINDOW_MS = 24 * 3_600_000;

/**
 * Health for one device.
 *
 * Lifted from the panel console's `healthFor()`, with the socket fast path the
 * server itself uses (rest-api.md: "`online` if the socket is open **or**
 * `last_seen` gap < 5 min"):
 *
 *   socket open              ⇒ online
 *   never seen               ⇒ unseen
 *   gap < 5 min              ⇒ online   (tolerates a Wi-Fi flap; a phone that
 *                                        drops its socket walking between APs
 *                                        is not an incident)
 *   gap < 24 h               ⇒ stale
 *   otherwise                ⇒ offline
 *
 * `now` is the shared clock, not `Date.now()` — a hundred rows must not run a
 * hundred timers to age the same number.
 */
export function healthFor(
  device: { online?: boolean; last_seen_at?: string | null },
  now: number,
): DeviceHealth {
  if (device.online) return "online";
  if (!device.last_seen_at) return "unseen";
  const seen = new Date(device.last_seen_at).getTime();
  if (Number.isNaN(seen)) return "unseen";
  const gap = now - seen;
  if (gap < ONLINE_WINDOW_MS) return "online";
  if (gap < STALE_WINDOW_MS) return "stale";
  return "offline";
}

const VARIANT: Record<DeviceHealth, PillVariant> = {
  online: "success",
  stale: "warn",
  offline: "danger",
  unseen: "muted",
};

export function DeviceHealthPill({
  device,
  showLastSeen = false,
  size = "md",
}: {
  device: { online?: boolean; last_seen_at?: string | null; health?: DeviceHealth };
  /** Append "· 3 分鐘前" inline. Off in dense tables that already have the column. */
  showLastSeen?: boolean;
  size?: "sm" | "md";
}) {
  const now = useNow();
  // The server computes `health` too; trust it when present, since it can see
  // the socket. Recompute locally only as a fallback, and to keep a live row
  // ageing between polls.
  const derived = healthFor(device, now);
  const health = device.online ? "online" : (device.health ?? derived);

  const tip = (
    <div className="space-y-0.5">
      <div>{healthDescription(health)}</div>
      {device.last_seen_at && (
        <div className="font-mono text-[10px] text-[var(--color-fg-faint)]">
          {absoluteTime(device.last_seen_at)}
        </div>
      )}
    </div>
  );

  return (
    <Tooltip content={tip}>
      <Pill variant={VARIANT[health]} size={size}>
        {healthLabel(health)}
        {showLastSeen && health !== "online" && device.last_seen_at && (
          <span className="text-[var(--color-fg-faint)]">· {timeAgo(device.last_seen_at, now)}</span>
        )}
      </Pill>
    </Tooltip>
  );
}
